import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { StaffName } from "../components/StaffName";
import { Users, Radio, Megaphone, MessageSquare, HeartHandshake, Activity, Play, LogIn } from "lucide-react";

const FEATURES = [
  { icon: Megaphone, title: "disc.f1t", text: "disc.f1x" },
  { icon: MessageSquare, title: "disc.f2t", text: "disc.f2x" },
  { icon: HeartHandshake, title: "disc.f3t", text: "disc.f3x" },
];

const STATUS_COLOR = { online: "#23A55A", idle: "#F0B232", dnd: "#F23F43", invisible: "#80848E" };

function CountUp({ to }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (to == null) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / 1200);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{v}</>;
}

export default function DiscordPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);
  useEffect(() => {
    const load = () => api.get("/discord/pulse").then(({ data }) => setPulse(data)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (!user) { setMembership(null); return; }
    api.get("/discord/my-membership").then(({ data }) => setMembership(data)).catch(() => setMembership(null));
  }, [user]);

  const showInvite = !!user && membership && (!membership.linked || membership.in_guild === false) && config?.discord_invite_url;

  const total = pulse?.members_total ?? 0;
  const onlineCount = pulse?.online_count ?? 0;
  const pct = total ? Math.min(100, Math.round((onlineCount / total) * 100)) : 0;

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("discord.over")}</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-4 flex items-center gap-5" data-testid="discord-page-title">
            <img src="/discord-logo.png" alt="Discord" className="w-14 h-14 md:w-20 md:h-20 object-contain" />
            <span>{t("discord.title1")}<br />{t("discord.title2")}</span>
          </h1>
          <p className="text-steel text-base md:text-lg max-w-xl mb-10">{t("discord.text")}</p>
        </Reveal>

        {/* Invito mirato: loggato sul sito ma fuori dal server */}
        {showInvite && (
          <Reveal>
            <div className="chamfer border border-[#5865F2]/60 bg-[#5865F2]/10 px-6 py-6 mb-10 flex flex-wrap items-center gap-5 justify-between" data-testid="discord-targeted-invite">
              <div className="flex items-center gap-4">
                <span className="chamfer-sm bg-[#5865F2] p-3"><LogIn className="w-5 h-5 text-white" /></span>
                <div>
                  <p className="font-display text-2xl uppercase text-white">{t("pulse.missing_title")}</p>
                  <p className="font-mono text-[11px] text-steel max-w-md leading-relaxed">{t("pulse.missing_sub")}</p>
                </div>
              </div>
              <a href={config.discord_invite_url} target="_blank" rel="noopener noreferrer"
                 className="discord-cta chamfer-sm bg-[#5865F2] hover:bg-[#4752c4] text-white px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-all duration-300 hover:-translate-y-0.5"
                 data-testid="discord-targeted-invite-button">
                <Users className="w-4 h-4" /> {t("pulse.missing_cta")}
              </a>
            </div>
          </Reveal>
        )}

        {/* E. Banner LIVE pulsante */}
        {pulse?.is_live && (
          <Reveal>
            <a href={`https://twitch.tv/ilquartiermastro`} target="_blank" rel="noopener noreferrer"
               className="chamfer block mb-10 border border-wardogs/70 bg-wardogs/10 px-6 py-5 relative overflow-hidden group"
               data-testid="discord-live-banner">
              <div className="absolute inset-0 bg-wardogs/10 animate-pulse" />
              <div className="relative flex flex-wrap items-center gap-4 justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-wardogs animate-blink" />
                  <div>
                    <p className="font-display text-2xl uppercase text-wardogs">{t("pulse.live_banner")}</p>
                    <p className="font-mono text-[11px] text-steel">{pulse.live_title} — {t("pulse.live_sub")}</p>
                  </div>
                </div>
                <span className="chamfer-sm bg-wardogs text-[#050505] px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] flex items-center gap-2 group-hover:bg-white transition-colors duration-300">
                  <Play className="w-3.5 h-3.5" /> {t("pulse.watch")}
                </span>
              </div>
            </a>
          </Reveal>
        )}

        {/* D. Contatori animati */}
        {pulse && (
          <Reveal>
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-7 mb-14" data-testid="discord-counters">
              <div className="flex flex-wrap items-end gap-x-14 gap-y-6">
                <div>
                  <p className="font-display text-6xl leading-none text-white"><CountUp to={total} /></p>
                  <p className="hud-label mt-2">{t("pulse.members")}</p>
                </div>
                <div>
                  <p className="font-display text-6xl leading-none text-[#23A55A]"><CountUp to={onlineCount} /></p>
                  <p className="hud-label mt-2">{t("pulse.online")}</p>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="h-2.5 bg-[#1A1A1A] chamfer-sm overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#23A55A] to-wardogs transition-all duration-1000" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="font-mono text-[10px] text-[#4A4D51] mt-2 uppercase tracking-[0.15em]">{pct}% · {t("pulse.realtime")}</p>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        <div className="grid gap-12 lg:grid-cols-2 items-start mb-16">
          <Reveal>
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-1 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-[#5865F2]/60" />
              {config?.discord_server_id ? (
                <iframe
                  src={`https://discord.com/widget?id=${config.discord_server_id}&theme=dark`}
                  title="Discord community"
                  className="w-full h-[480px]"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                  data-testid="discord-widget"
                />
              ) : (
                <div className="h-[480px] flex flex-col items-center justify-center gap-4" data-testid="discord-widget-placeholder">
                  <Users className="w-10 h-10 text-[#2A2A2A]" />
                  <p className="hud-label text-center px-8">{t("discord.radar")}</p>
                </div>
              )}
            </div>
          </Reveal>

          <div className="flex flex-col gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={0.1 + i * 0.08}>
                <div className="chamfer border border-[#1A1A1A] bg-[#050505] p-7 flex gap-5" data-testid={`discord-feature-${i + 1}`}>
                  <f.icon className="w-6 h-6 text-wardogs shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl uppercase leading-tight mb-2">{t(f.title)}</h3>
                    <p className="text-sm text-steel leading-relaxed">{t(f.text)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              {config?.discord_invite_url ? (
                <a
                  href={config.discord_invite_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="discord-join-button"
                  className="discord-cta chamfer inline-flex items-center gap-3 bg-[#5865F2] hover:bg-[#4752c4] text-white font-mono text-xs uppercase tracking-[0.25em] font-bold px-8 py-5 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <img src="/discord-logo.png" alt="Discord" className="w-4 h-4 object-contain" /> {t("discord.join")}
                </a>
              ) : (
                <div data-testid="discord-pending-notice" className="chamfer border border-[#2A2A2A] px-6 py-4 inline-flex items-center gap-3">
                  <Radio className="w-4 h-4 text-wardogs animate-blink" />
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-steel">{t("discord.pending")}</span>
                </div>
              )}
            </Reveal>
          </div>
        </div>

        {/* A. Chi c'è adesso */}
        {pulse && pulse.online.length > 0 && (
          <Reveal>
            <div className="mb-16" data-testid="discord-online-now">
              <div className="flex items-center gap-3 mb-6">
                <Activity className="w-5 h-5 text-[#23A55A]" />
                <h2 className="font-display text-4xl uppercase">{t("pulse.online_title")}</h2>
              </div>
              <p className="font-mono text-[11px] text-steel mb-6">{t("pulse.online_sub")}</p>
              <div className="flex flex-wrap gap-3">
                {pulse.online.map((m, i) => (
                  <div key={i} className="chamfer-sm border border-[#1A1A1A] bg-[#0C0C0C] pl-1.5 pr-4 py-1.5 flex items-center gap-2.5 hover:border-[#2A2A2A] transition-colors duration-300" data-testid={`online-member-${i}`}>
                    <div className="relative">
                      <img src={m.avatar} alt="" className="w-9 h-9 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0C0C0C]" style={{ background: STATUS_COLOR[m.status] || STATUS_COLOR.online }} />
                    </div>
                    {m.is_staff ? (
                      <StaffName name={m.name} className="font-mono text-xs font-bold" />
                    ) : (
                      <span className="font-mono text-xs text-white">{m.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
